import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    FEATURE_FLIGHT_CONFORMANCE,
    FEATURE_GENERIC_TASKS,
    MissionService,
    OrganisationAuthorityService,
    PersonsOrganisationDto,
    PreferencesService,
    SimpleAuthorityDto,
    UserService,
    WorkTracker,
    hasFeatureFlag,
    observeFormControl
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDateTimePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { toSimpleAuthority } from 'libs/authorities/src/lib/helpers';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { EMPTY, Subject, combineLatest } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    MissionFilterOptions,
    UpcomingMissionData,
    UpcomingMissionsDataService
} from '../upcoming-mission-data.service';

const UPCOMING_MISSIONS_IDENTIFIER = 'upcomingMissionsWidget';

/**
 * This widget will display upcoming missions where the current user is
 * part of the mission crew.
 */
@Component({
    selector: 'upcoming-missions-widget',
    templateUrl: './upcoming-missions-widget.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [UpcomingMissionsDataService]
})
export class UpcomingMissionsWidgetComponent implements OnInit, OnDestroy {
    organisation: PersonsOrganisationDto;
    showBody = true;
    canUseFlightConformance = false;

    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    gqlSorting: ColumnSortPreferences;

    missions: UpcomingMissionData[] = [];
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    formGroup = new FormGroup({
        filterSelection: new FormControl<MissionFilterOptions>('UPCOMING')
    });
    modeGuidanceText = 'Future missions where you are part of the flight crew';

    private organisationAuthorities: SimpleAuthorityDto[] = [];
    private authorityIds: number[] = [];

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private upcomingMissionsDataService: UpcomingMissionsDataService,
        private workspaceStateService: WorkspaceStateService,
        private missionService: MissionService,
        private missionDialoguesService: MissionDialoguesService,
        private formatDateTimePipe: FormatDateTimePipe,
        private preferencesService: PreferencesService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private userService: UserService
    ) {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Mission',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewMission(row)
            },
            {
                value: 'convertedMissionDate',
                name: 'Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => this.formatDateTimePipe.transform(t)
            },
            {
                value: 'rpic',
                name: 'Pilot',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'rpaNicknames',
                name: 'RPA',
                searchable: false,
                selectable: true,
                sortable: false,
                defaultSelection: true
            },
            {
                value: 'locationName',
                name: 'Location',
                searchable: false,
                selectable: true,
                defaultSelection: false
            },
            {
                value: 'workflowName',
                name: 'Workflow',
                searchable: false,
                selectable: true,
                defaultSelection: false
            }
        ];

        this.selectedColumns = ['rpaNicknames'];

        this.tableConfig = {
            serverPagination: true,
            currentPage: 0,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.gqlSorting = null;

        combineLatest([
            this.upcomingMissionsDataService.working$,
            this.workTracker.observable
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([serviceWorking, working]) =>
                    (this.working = working || serviceWorking)
            );
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.canUseFlightConformance = hasFeatureFlag(
                    this.organisation,
                    FEATURE_FLIGHT_CONFORMANCE
                );
                this.setupMissionAuthorities();
            });

        observeFormControl(this.formGroup.controls.filterSelection)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.tableConfig = {
                    ...this.tableConfig,
                    currentPage: 0
                };
                this.setGuidanceText();
                this.refreshMissions();
            });

        combineLatest([
            this.upcomingMissionsDataService.missions$,
            this.upcomingMissionsDataService.currentPage$,
            this.upcomingMissionsDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([missions, currentPage, totalItems]) => {
                this.missions = missions;
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.missionService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshMissions());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private setGuidanceText() {
        const mode = this.formGroup.controls.filterSelection.value;
        switch (mode) {
            case 'UPCOMING':
                this.modeGuidanceText =
                    'Future missions where you are part of the flight crew.';
                break;

            case 'ACTIVATED':
                this.modeGuidanceText =
                    'Missions that are currently activated in the Field App and have not yet been completed.';
                break;

            case 'INACTIVE_APPROVALS':
                this.modeGuidanceText =
                    'Upcoming planned missions with cancelled or revoked approvals that may require attention.';
                break;

            case 'CONFORMANCE_BREACHES':
                this.modeGuidanceText =
                    'Your missions with conformance breaches that may require attention.';
                break;

            default:
                this.modeGuidanceText =
                    'Select one of the items above to begin';
                break;
        }
    }

    private setupMissionAuthorities() {
        if (!this.organisation) {
            return;
        }

        this.organisationAuthorityService
            .findAuthorities(this.organisation.id, this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authorities => {
                    const organisationAuthorities = authorities
                        .filter(group => group.hasWorkflow)
                        .map(group =>
                            group.authorities.map(auth =>
                                toSimpleAuthority(
                                    auth,
                                    this.organisation,
                                    group
                                )
                            )
                        )
                        .reduce((acc, auths) => [...acc, ...auths], []);

                    this.organisationAuthorities = organisationAuthorities;
                    this.authorityIds = organisationAuthorities.map(a => a.id);

                    this.loadPreferences();
                },
                error: error => {
                    console.error('Error loading authorities:', error);
                    this.loadPreferences();
                }
            })
            .add(this.workTracker.createTracker());
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'upcomingMissionsWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            })
            .add(this.workTracker.createTracker());
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3;
        this.applyPreferences({});
    }

    refreshMissions() {
        if (!this.organisation) {
            return;
        }

        const personId = this.userService.getCurrentUser()?.id;
        const filter =
            this.formGroup.controls.filterSelection.value ?? 'UPCOMING';

        this.upcomingMissionsDataService.findMissions(
            this.tableConfig.currentPage ?? 0,
            this.tableConfig.limit ?? 3,
            this.gqlSorting,
            this.organisation.id,
            personId,
            this.authorityIds,
            filter
        );
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.gqlSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
        this.refreshMissions();
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'upcomingMissionsWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    viewMission(mission: UpcomingMissionData) {
        this.missionDialoguesService.showMissionDetails(mission.id, true);
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshMissions();
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.gqlSorting != null &&
            !this.selectedColumns.includes(this.gqlSorting.column)
        ) {
            this.gqlSorting = null;
        }
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.gqlSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.gqlSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }
}

export const upcomingMissionTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: UPCOMING_MISSIONS_IDENTIFIER,
        section: 'operations',
        component: UpcomingMissionsWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_GENERIC_TASKS),
        requiredPermissions: [PersonsOrganisationDto.Permissions.MISSION_VIEW]
    };
